// src/services/notificationService.js
import apiClient from "./api"; // Assuming your configured axios instance is named apiClient

const API_BASE = "/api/notifications"; // Base path for notification endpoints

/**
 * Fetches notifications from the backend.
 * @returns {Promise<Array<object>>} Array of notification objects.
 * @throws {Error} If fetching fails.
 */
export const getNotifications = async () => {
  try {
    const response = await apiClient.get(API_BASE);
    // Backend returns sorted by createdAt descending, matching mock expectation
    // The backend returns objects with _id, map to id if frontend strictly requires it
    return response.data.map((n) => ({ ...n, id: n._id })); // Map _id to id
  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch notifications.";
    console.error(
      "getNotifications service error:",
      message,
      error.response?.data
    );
    throw new Error(message);
  }
};

/**
 * Marks a single notification as read via the API.
 * @param {string} notificationId - The ID (_id) of the notification to mark as read.
 * @returns {Promise<object>} The updated notification object from the backend.
 * @throws {Error} If marking as read fails.
 */
export const markNotificationRead = async (notificationId) => {
  try {
    // Note the endpoint structure: /:id/read
    const response = await apiClient.patch(
      `${API_BASE}/${notificationId}/read`
    );
    return { ...response.data, id: response.data._id }; // Map _id to id
  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.message ||
      "Failed to mark notification as read.";
    console.error(
      "markNotificationRead service error:",
      message,
      error.response?.data
    );
    throw new Error(message);
  }
};

/**
 * Marks all notifications as read via the API.
 * @returns {Promise<{success: boolean, modifiedCount: number, matchedCount: number}>} Confirmation object from the backend.
 * @throws {Error} If the operation fails.
 */
export const markAllNotificationsRead = async () => {
  try {
    const response = await apiClient.patch(`${API_BASE}/mark-all-read`);
    return response.data; // Backend returns { success, modifiedCount, matchedCount }
  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.message ||
      "Failed to mark all notifications as read.";
    console.error(
      "markAllNotificationsRead service error:",
      message,
      error.response?.data
    );
    throw new Error(message);
  }
};

/**
 * Deletes a notification via the API.
 * @param {string} notificationId - The ID (_id) of the notification to delete.
 * @returns {Promise<{success: boolean, id: string}>} Confirmation object from the backend.
 * @throws {Error} If deletion fails.
 */
export const deleteNotification = async (notificationId) => {
  try {
    const response = await apiClient.delete(`${API_BASE}/${notificationId}`);
    return response.data; // Backend returns { success: true, id: notificationId }
  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.message ||
      "Failed to delete notification.";
    console.error(
      "deleteNotification service error:",
      message,
      error.response?.data
    );
    throw new Error(message);
  }
};

// Note: The function to *create* a notification (`addQuoteNotification` in mockData)
// is now handled on the *backend*. When an event happens (like a quote being added),
// the backend controller for that event should call the `createNotification` helper function.
// The frontend doesn't directly create notifications; it triggers actions that cause
// the backend to create them. The frontend then fetches the latest notifications using `getNotifications`.
